import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  HeartIcon, 
  StarIcon,
  ShoppingCartIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { FaTag } from 'react-icons/fa';
import './ProductCard.css';
import { Product } from '../features/products/productModels';
import { CategoryProduct } from '../features/categories/categoryModels';
import { useCart } from '../contexts/CartContext';

interface ProductCardProps {
  product: Product|CategoryProduct;
  onAddToCart: (product: Product|CategoryProduct) => void;
  onToggleWishlist: (productId: number) => void;
  isInWishlist: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onAddToCart,
  onToggleWishlist,
  isInWishlist
}) => {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const [isHovered, setIsHovered] = useState(false);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (product) {
      addToCart(product);
    }
  };

  const handleToggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // onToggleWishlist(product._id);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <StarIcon
        key={index}
        className={`star ${index < Math.floor(rating) ? 'filled' : ''}`}
      />
    ));
  };

  return (
    <div 
      className={`product-card ${isHovered ? 'hovered' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link to={`/product/${product._id}`} className="product-link">
        {/* Product Image */}
        <div className="product-image-container">
          <img 
            src={product.images?.[0] || product.imagePath} 
            alt={product.productName}
            className="product-image"
          />
          
          {/* Badges */}
          <div className="product-badges">
            {product.enable && <span className="badge new">{t('productCard.new')}</span>}
            {/* add me a sale badge here or discount badge here if the product is on sale with icon of discount */}
            {product.sale && <span className="badge discount">%</span>}  
            {/* {product.sale && <span className="badge bestseller">Best Seller</span>} */}
          </div>

          {/* Quick Actions */}
          <div className="quick-actions">
            <button
              className={`quick-action-button wishlist ${isInWishlist ? 'active' : ''}`}
              onClick={handleToggleWishlist}
              title={isInWishlist ? t('productCard.removeFromWishlist') : t('productCard.addToWishlist')}
            >
              {isInWishlist ? (
                <HeartSolidIcon className="action-icon" />
              ) : (
                <HeartIcon className="action-icon" />
              )}
            </button>
            <button
              className="quick-action-button view"
              title={t('productCard.quickView')}
            >
              <EyeIcon className="action-icon" />
            </button>
          </div>

          {/* Stock Status */}
          {!product.enable && (
            <div className="out-of-stock-overlay">
              <span>{t('productCard.outOfStock')}</span>
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="product-info">
          {/* Brand */}
          <div className="product-brand">{product.brand}-{product.model}</div>
          {/* Product Name */}
          <h3 className="product-name">{product.productName}</h3>
          {/* Price */}
          <div className="product-price">
            <span className="current-price">₪{ product?.salePrice && product?.salePrice>0 && product.sale ? product.salePrice : product.price}</span>
            {product.sale && (
              <span className="original-price">₪{product.price}</span>
            )}
          </div>
          {/* <p className="product-description">{product.}</p> */}
          {/* Add to Cart Button */}
          <button
            className={`add-to-cart-button ${!product.enable ? 'disabled' : ''}`}
            onClick={handleAddToCart}
            disabled={!product.enable}
          >
            <ShoppingCartIcon className="cart-icon" />
            {product.enable ? t('productCard.addToCart') : t('productCard.outOfStock')}
          </button>
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;
